import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RefreshCw, Package, Calendar, MapPin, Zap, Play, X, AlertCircle, CheckCircle, Clock, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { ordersService, OrderWithLinesDto, OrderLineDto, UpdateOrderRequest } from '../../../../services/ordersService';
import ruleGroupEngineService, { SystemRuleGroup, RuleGroupParameters, RuleGroupResult, ConsolidationCheckResult } from '../../../../services/ruleGroupEngineService';
import { systemSetupService, SystemSetupDto } from '../../../../services/systemSetupService';
import { filterGroupService } from '../../../../services/filterGroupService';
import { FilterGroupDto } from '../../../../types/filterGroup';
import { deliveryMethodService, DeliveryMethodDto } from '../../../../services/deliveryMethodService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import { OrderTimelineModal } from '../order-timeline';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow, RowAction } from '../../../shared/table';

// Editable field names (matches keys in UpdateOrderRequest)
type EditableTextField =
  | 'filterGroupID' | 'district' | 'projectNo' | 'depNo' | 'ourRef' | 'carrierName'
  | 'orderFreeField1' | 'orderFreeField2' | 'orderFreeField3' | 'orderFreeField4'
  | 'orderFreeField5' | 'orderFreeField6' | 'orderFreeField7' | 'orderFreeField8'
  | 'orderFreeField9' | 'orderFreeField10' | 'orderFreeField11' | 'orderFreeField12'
  | 'orderFreeField13' | 'orderFreeField14' | 'orderFreeField15' | 'orderFreeField16'
  | 'pListFreeField1' | 'pListFreeField2' | 'pListFreeField3';
type EditableNumberField = 'districtNo' | 'orderPriority' | 'customerPriority';
type EditableDateField = 'deliveryDate' | 'arrivalDate';
type EditableField = EditableTextField | EditableNumberField | EditableDateField;

// Extended type for table - includes all OrderDto fields plus lineCount
interface OrderTableRow extends TableDataRow {
  id: number;
  dbId: number; // Database ID needed for delete API
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  pickListNoERP: string | null;
  orderType: string | null;
  orderStatusERP: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  customerNo: string;
  customerName: string | null;
  delAddressNo: number | null;
  delCustomerName: string | null;
  delAddress1: string | null;
  delAddress2: string | null;
  delAddress3: string | null;
  delZipCode: string | null;
  delCity: string | null;
  delCountryCode: string | null;
  delCountryName: string | null;
  deliveryGLN: string | null;
  customerOrderNo: string | null;
  yourRef: string | null;
  ourRef: string | null;
  projectNo: string | null;
  depNo: string | null;
  deliveryMethod: string | null;
  temsOfDelivery: string | null;
  shipmentType: string | null;
  carrierName: string | null;
  termsOfPayment: string | null;
  employee: string | null;
  orderPriority: number | null;
  customerPriority: number | null;
  district: string | null;
  contactName: string | null;
  orderTotal: number | null;
  customerType: string | null;
  customerGrp1: string | null;
  customerGrp2: string | null;
  customerGrp3: string | null;
  isRemainderYesNo: boolean | null;
  createRemainderYesNo: boolean | null;
  emailAddress: string | null;
  orderDeliveryYear: number | null;
  orderDeliveryMonth: number | null;
  orderDeliveryDay: number | null;
  orderDeliveryWeek: number | null;
  orderFreeField1: string | null;
  orderFreeField2: string | null;
  orderFreeField3: string | null;
  orderFreeField4: string | null;
  orderFreeField5: string | null;
  orderFreeField6: string | null;
  orderFreeField7: string | null;
  orderFreeField8: string | null;
  orderFreeField9: string | null;
  orderFreeField10: string | null;
  orderFreeField11: string | null;
  orderFreeField12: string | null;
  orderFreeField13: string | null;
  orderFreeField14: string | null;
  orderFreeField15: string | null;
  orderFreeField16: string | null;
  customerDeliveryDay: string | null;
  telephone: string | null;
  mobTelephone: string | null;
  tripID: number | null;
  districtNo: number | null;
  eventCustomerRef1: string | null;
  eventCustomerRef2: string | null;
  eventCustomerRef3: string | null;
  emailNotificationAddress: string | null;
  smsNotificationAddress: string | null;
  intEmailNotificationAddress: string | null;
  intSMSNotificationAddress: string | null;
  distributionYesNo: boolean | null;
  deliveryAlias: string | null;
  filterGroupID: string | null;
  pListFreeField1: string | null;
  pListFreeField2: string | null;
  pListFreeField3: string | null;
  shipmentID: number | null;
  clientID: string | null;
  loadingName: string | null;
  loadingAddress1: string | null;
  loadingZipCode: string | null;
  loadingCity: string | null;
  loadingCountry: string | null;
  dropPointID: string | null;
  warehouse: string | null;
  arrivalDate: string | null;
  deliveryMethodNo: number | null;
  consignmentSWProductNo: string | null;
  updatedBy: string | null;
  lastUpdate: string | null;
  createdBy: string | null;
  created: string | null;
  lineCount: number;
  lines: OrderLineDto[];
}

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<OrderTableRow>();

interface NewOrdersProps {
  onBack?: () => void;
}

const NewOrders: React.FC<NewOrdersProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const [orders, setOrders] = useState<OrderWithLinesDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  // Order-level selection (not line-level)
  const [selectedOrderNos, setSelectedOrderNos] = useState<number[]>([]);

  // Rule execution state
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [ruleGroups, setRuleGroups] = useState<SystemRuleGroup[]>([]);
  const [stdRuleGroupID, setStdRuleGroupID] = useState<number | null>(null);
  const [selectedRuleGroupId, setSelectedRuleGroupId] = useState<number>(0);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [executingRule, setExecutingRule] = useState(false);
  const [ruleResult, setRuleResult] = useState<RuleGroupResult | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  // Konsoliderings-spørsmål: satt når sjekken finner at valgte ordrer ville blitt slått sammen
  const [consolidationCheck, setConsolidationCheck] = useState<ConsolidationCheckResult | null>(null);
  const [timelineOrderNo, setTimelineOrderNo] = useState<number | null>(null);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'order' | 'line'; id: number; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filter groups for dropdown
  const [filterGroups, setFilterGroups] = useState<FilterGroupDto[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodDto[]>([]);
  // Kode-baserte, språk-bevisste filteropsjoner for status-kolonnen (StatusType 1).
  const orderStatusOptions = useStatusOptions(1);

  // Inline editing state
  const [editState, setEditState] = useState<Record<number, Partial<Record<EditableField, string | number>>>>({});
  const [savingOrders, setSavingOrders] = useState<Set<number>>(new Set());
  const [savedOrders, setSavedOrders] = useState<Set<number>>(new Set());
  const editStateRef = useRef(editState);
  const ordersRef = useRef(orders);

  // Keep refs in sync
  useEffect(() => { editStateRef.current = editState; }, [editState]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // Save a single field inline edit via API
  const saveInlineEdit = useCallback(async (
    orderNo: number,
    field: EditableField,
    newFieldValue?: string | number
  ) => {
    const orderWithLines = ordersRef.current.find(o => o.header.orderNo === orderNo);
    if (!orderWithLines) return;

    const editData = editStateRef.current[orderNo];
    let newValue: string | number | undefined;

    if (newFieldValue !== undefined) {
      newValue = newFieldValue;
    } else if (editData) {
      newValue = editData[field];
    } else {
      return;
    }

    // Check if value actually changed
    const oldValue = (orderWithLines.header as any)[field] ?? '';
    if (String(newValue) === String(oldValue)) return;

    try {
      setSavingOrders(prev => new Set(prev).add(orderNo));

      // Build partial update request with only the changed field
      const request: UpdateOrderRequest = {};
      (request as any)[field] = newValue === '' ? null : newValue;

      const updatedOrder = await ordersService.updateOrder(orderNo, request);

      // Update local state with response
      setOrders(prev => prev.map(o =>
        o.header.orderNo === orderNo
          ? { ...o, header: { ...o.header, ...updatedOrder } }
          : o
      ));

      // Show saved indicator
      setSavedOrders(prev => new Set(prev).add(orderNo));
      setTimeout(() => {
        setSavedOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(orderNo);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`${t('manager.modules.newOrders.alerts.saveError', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.newOrders.alerts.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setSavingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  }, [t]);

  // Generic renderer for editable text cells
  const renderEditableText = useCallback((field: EditableTextField) => {
    return (value: string | null, row: OrderTableRow) => {
      const orderNo = row.orderNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingOrders.has(orderNo);
      const isSaved = savedOrders.has(orderNo);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${orderNo}-${value}`}
            type="text"
            defaultValue={value || ''}
            onChange={(e) => {
              e.stopPropagation();
              setEditState(prev => ({
                ...prev,
                [orderNo]: { ...prev[orderNo], [field]: e.target.value }
              }));
            }}
            onBlur={() => saveInlineEdit(orderNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(orderNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingOrders, savedOrders, saveInlineEdit]);

  // Generic renderer for editable number cells
  const renderEditableNumber = useCallback((field: EditableNumberField) => {
    return (value: number | null, row: OrderTableRow) => {
      const orderNo = row.orderNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingOrders.has(orderNo);
      const isSaved = savedOrders.has(orderNo);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${orderNo}-${value}`}
            type="number"
            defaultValue={value ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const numVal = e.target.value === '' ? '' : parseInt(e.target.value);
              setEditState(prev => ({
                ...prev,
                [orderNo]: { ...prev[orderNo], [field]: numVal }
              }));
            }}
            onBlur={() => saveInlineEdit(orderNo, field)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                saveInlineEdit(orderNo, field);
                (e.target as HTMLInputElement).blur();
              }
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs text-right border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingOrders, savedOrders, saveInlineEdit]);

  // Generic renderer for editable date cells
  const renderEditableDate = useCallback((field: EditableDateField) => {
    return (value: string | null, row: OrderTableRow) => {
      const orderNo = row.orderNo;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingOrders.has(orderNo);
      const isSaved = savedOrders.has(orderNo);

      // Convert ISO date to yyyy-MM-dd for input
      const dateValue = value ? value.substring(0, 10) : '';

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <input
            key={`${field}-${orderNo}-${dateValue}`}
            type="date"
            defaultValue={dateValue}
            onChange={(e) => {
              e.stopPropagation();
              const isoValue = e.target.value ? `${e.target.value}T00:00:00` : '';
              setEditState(prev => ({
                ...prev,
                [orderNo]: { ...prev[orderNo], [field]: isoValue }
              }));
              // Save immediately on date change (picker closes on select)
              saveInlineEdit(orderNo, field, isoValue || '');
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          />
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingOrders, savedOrders, saveInlineEdit]);

  // Renderer for filterGroupID dropdown
  const renderFilterGroupDropdown = useCallback((value: string | null, row: OrderTableRow) => {
    const orderNo = row.orderNo;
    const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
    const isSaving = savingOrders.has(orderNo);
    const isSaved = savedOrders.has(orderNo);

    return (
      <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
        <select
          key={`filterGroupID-${orderNo}-${value}-${filterGroups.length}`}
          defaultValue={value || ''}
          onChange={(e) => {
            e.stopPropagation();
            const newVal = e.target.value;
            setEditState(prev => ({
              ...prev,
              [orderNo]: { ...prev[orderNo], filterGroupID: newVal }
            }));
            // Save immediately on select change
            saveInlineEdit(orderNo, 'filterGroupID', newVal);
          }}
          onClick={stopAll}
          onMouseDown={stopAll}
          onFocus={stopAll}
          disabled={isSaving}
          className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
            isSaving ? 'opacity-50' : ''
          } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
        >
          <option value="">{t('manager.modules.newOrders.filterGroup.none', {}, '— Ingen —')}</option>
          {filterGroups.map(fg => (
            <option key={fg.filterGroupID} value={fg.filterGroupID}>
              {fg.filterGroupID} - {fg.filterGroupDescr || ''}
            </option>
          ))}
        </select>
        {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
      </div>
    );
  }, [savingOrders, savedOrders, saveInlineEdit, filterGroups, t]);

  // Save delivery method — sets both DeliveryMethodNo (FK) and DeliveryMethod (description) in one request
  const saveDeliveryMethod = useCallback(async (orderNo: number, deliveryMethodNo: number | null) => {
    const orderWithLines = ordersRef.current.find(o => o.header.orderNo === orderNo);
    if (!orderWithLines) return;

    // No-op if unchanged
    if ((orderWithLines.header.deliveryMethodNo ?? null) === deliveryMethodNo) return;

    const selected = deliveryMethods.find(dm => dm.deliveryMethodNo === deliveryMethodNo);
    const request: UpdateOrderRequest = {
      deliveryMethodNo: deliveryMethodNo,
      deliveryMethod: selected?.description ?? null
    };

    try {
      setSavingOrders(prev => new Set(prev).add(orderNo));

      const updatedOrder = await ordersService.updateOrder(orderNo, request);

      setOrders(prev => prev.map(o =>
        o.header.orderNo === orderNo
          ? { ...o, header: { ...o.header, ...updatedOrder } }
          : o
      ));

      setSavedOrders(prev => new Set(prev).add(orderNo));
      setTimeout(() => {
        setSavedOrders(prev => {
          const newSet = new Set(prev);
          newSet.delete(orderNo);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Feil ved lagring:', err);
      alert(`${t('manager.modules.newOrders.alerts.saveError', {}, 'Feil ved lagring')}: ${err instanceof Error ? err.message : t('manager.modules.newOrders.alerts.unknownError', {}, 'Ukjent feil')}`);
    } finally {
      setSavingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  }, [deliveryMethods, t]);

  // Renderer for delivery method dropdown (used by both the "Leveringsmåte" and "Lev.måte nr" columns).
  // Both columns share the DeliveryMethods masterdata and update each other on change.
  const renderDeliveryMethodDropdown = useCallback((displayMode: 'description' | 'number') => {
    return (_value: unknown, row: OrderTableRow) => {
      const orderNo = row.orderNo;
      const currentNo = row.deliveryMethodNo ?? null;
      const stopAll = (e: React.SyntheticEvent) => e.stopPropagation();
      const isSaving = savingOrders.has(orderNo);
      const isSaved = savedOrders.has(orderNo);

      return (
        <div className="flex items-center space-x-1" onClick={stopAll} onMouseDown={stopAll}>
          <select
            key={`deliveryMethod-${orderNo}-${currentNo}-${deliveryMethods.length}`}
            defaultValue={currentNo ?? ''}
            onChange={(e) => {
              e.stopPropagation();
              const val = e.target.value;
              saveDeliveryMethod(orderNo, val === '' ? null : Number(val));
            }}
            onClick={stopAll}
            onMouseDown={stopAll}
            onFocus={stopAll}
            disabled={isSaving}
            className={`w-full px-2 py-1 text-xs border rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 bg-white ${
              isSaving ? 'opacity-50' : ''
            } ${isSaved ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
          >
            <option value="">{t('manager.modules.newOrders.deliveryMethod.none', {}, '— Velg —')}</option>
            {deliveryMethods.map(dm => (
              <option key={dm.deliveryMethodNo} value={dm.deliveryMethodNo}>
                {displayMode === 'number'
                  ? dm.deliveryMethodNo
                  : `${dm.deliveryMethodNo} - ${dm.description || ''}`}
              </option>
            ))}
          </select>
          {isSaved && <span className="text-green-500 text-xs flex-shrink-0">&#10003;</span>}
        </div>
      );
    };
  }, [savingOrders, savedOrders, saveDeliveryMethod, deliveryMethods, t]);

  useEffect(() => {
    loadOrders();
    loadSystemSetupAndRuleGroups();
    loadFilterGroups();
    loadDeliveryMethods();
  }, []);

  const loadSystemSetupAndRuleGroups = async () => {
    if (!user?.companyNo) return;

    try {
      // Load system setup to get stdRuleGroupID
      const systemSetup = await systemSetupService.getCurrent();
      const configuredRuleGroupId = systemSetup.stdRuleGroupID;
      setStdRuleGroupID(configuredRuleGroupId ?? null);

      // Load all rule groups
      const allGroups = await ruleGroupEngineService.getRuleGroups(user.companyNo);

      // Filter to only show the configured rule group(s) from SystemSetup
      if (configuredRuleGroupId) {
        const filteredGroups = allGroups.filter(g => g.ruleGroupID === configuredRuleGroupId);
        setRuleGroups(filteredGroups);
        // Pre-select the configured rule group
        setSelectedRuleGroupId(configuredRuleGroupId);
      } else {
        // If no stdRuleGroupID is configured, show all (fallback)
        setRuleGroups(allGroups);
      }
    } catch (err) {
      console.error('Error loading system setup or rule groups:', err);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersService.getNewOrdersWithLines(currentWarehouse || undefined);
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.newOrders.errors.loadFailed', {}, 'Feil ved lasting av ordre'));
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterGroups = async () => {
    try {
      const groups = await filterGroupService.getCurrent();
      setFilterGroups(groups);
    } catch (err) {
      console.error('Error loading filter groups:', err);
    }
  };

  const loadDeliveryMethods = async () => {
    try {
      const methods = await deliveryMethodService.getAll();
      setDeliveryMethods(methods);
    } catch (err) {
      console.error('Error loading delivery methods:', err);
    }
  };

  // Transform orders to table-compatible format
  const tableData: OrderTableRow[] = useMemo(() => {
    return orders.map(orderWithLines => {
      const h = orderWithLines.header;
      return {
        id: h.orderNo,
        dbId: h.id,
        orderNo: h.orderNo,
        orderNoERP: h.orderNoERP,
        orderStatus: h.orderStatus,
        pickListNoERP: h.pickListNoERP || null,
        orderType: h.orderType || null,
        orderStatusERP: h.orderStatusERP || null,
        orderDate: h.orderDate || null,
        deliveryDate: h.deliveryDate || null,
        customerNo: h.customerNo,
        customerName: h.customerName || null,
        delAddressNo: h.delAddressNo || null,
        delCustomerName: h.delCustomerName || null,
        delAddress1: h.delAddress1 || null,
        delAddress2: h.delAddress2 || null,
        delAddress3: h.delAddress3 || null,
        delZipCode: h.delZipCode || null,
        delCity: h.delCity || null,
        delCountryCode: h.delCountryCode || null,
        delCountryName: h.delCountryName || null,
        deliveryGLN: h.deliveryGLN || null,
        customerOrderNo: h.customerOrderNo || null,
        yourRef: h.yourRef || null,
        ourRef: h.ourRef || null,
        projectNo: h.projectNo || null,
        depNo: h.depNo || null,
        deliveryMethod: h.deliveryMethod || null,
        temsOfDelivery: h.temsOfDelivery || null,
        shipmentType: h.shipmentType || null,
        carrierName: h.carrierName || null,
        termsOfPayment: h.termsOfPayment || null,
        employee: h.employee || null,
        orderPriority: h.orderPriority ?? null,
        customerPriority: h.customerPriority ?? null,
        district: h.district || null,
        contactName: h.contactName || null,
        orderTotal: h.orderTotal ?? null,
        customerType: h.customerType || null,
        customerGrp1: h.customerGrp1 || null,
        customerGrp2: h.customerGrp2 || null,
        customerGrp3: h.customerGrp3 || null,
        isRemainderYesNo: h.isRemainderYesNo ?? null,
        createRemainderYesNo: h.createRemainderYesNo ?? null,
        emailAddress: h.emailAddress || null,
        orderDeliveryYear: h.orderDeliveryYear ?? null,
        orderDeliveryMonth: h.orderDeliveryMonth ?? null,
        orderDeliveryDay: h.orderDeliveryDay ?? null,
        orderDeliveryWeek: h.orderDeliveryWeek ?? null,
        orderFreeField1: h.orderFreeField1 || null,
        orderFreeField2: h.orderFreeField2 || null,
        orderFreeField3: h.orderFreeField3 || null,
        orderFreeField4: h.orderFreeField4 || null,
        orderFreeField5: h.orderFreeField5 || null,
        orderFreeField6: h.orderFreeField6 || null,
        orderFreeField7: h.orderFreeField7 || null,
        orderFreeField8: h.orderFreeField8 || null,
        orderFreeField9: h.orderFreeField9 || null,
        orderFreeField10: h.orderFreeField10 || null,
        orderFreeField11: h.orderFreeField11 || null,
        orderFreeField12: h.orderFreeField12 || null,
        orderFreeField13: h.orderFreeField13 || null,
        orderFreeField14: h.orderFreeField14 || null,
        orderFreeField15: h.orderFreeField15 || null,
        orderFreeField16: h.orderFreeField16 || null,
        customerDeliveryDay: h.customerDeliveryDay || null,
        telephone: h.telephone || null,
        mobTelephone: h.mobTelephone || null,
        tripID: h.tripID ?? null,
        districtNo: h.districtNo ?? null,
        eventCustomerRef1: h.eventCustomerRef1 || null,
        eventCustomerRef2: h.eventCustomerRef2 || null,
        eventCustomerRef3: h.eventCustomerRef3 || null,
        emailNotificationAddress: h.emailNotificationAddress || null,
        smsNotificationAddress: h.smsNotificationAddress || null,
        intEmailNotificationAddress: h.intEmailNotificationAddress || null,
        intSMSNotificationAddress: h.intSMSNotificationAddress || null,
        distributionYesNo: h.distributionYesNo ?? null,
        deliveryAlias: h.deliveryAlias || null,
        filterGroupID: h.filterGroupID || null,
        pListFreeField1: h.pListFreeField1 || null,
        pListFreeField2: h.pListFreeField2 || null,
        pListFreeField3: h.pListFreeField3 || null,
        shipmentID: h.shipmentID ?? null,
        clientID: h.clientID || null,
        loadingName: h.loadingName || null,
        loadingAddress1: h.loadingAddress1 || null,
        loadingZipCode: h.loadingZipCode || null,
        loadingCity: h.loadingCity || null,
        loadingCountry: h.loadingCountry || null,
        dropPointID: h.dropPointID || null,
        warehouse: h.warehouse || null,
        arrivalDate: h.arrivalDate || null,
        deliveryMethodNo: h.deliveryMethodNo ?? null,
        consignmentSWProductNo: h.consignmentSWProductNo || null,
        updatedBy: h.updatedBy || null,
        lastUpdate: h.lastUpdate || null,
        createdBy: h.createdBy || null,
        created: h.created || null,
        lineCount: orderWithLines.lines.length,
        lines: orderWithLines.lines
      };
    });
  }, [orders]);

  // Handle selection change from WarehouseTable
  const handleSelectionChange = useCallback((selectedRows: (string | number)[], selectedData: OrderTableRow[]) => {
    setSelectedOrderNos(selectedRows as number[]);
  }, []);

  // Custom row ID function
  const getOrderRowId = useCallback((order: OrderTableRow) => order.orderNo, []);

  // Open rule execution modal
  const openRuleModal = useCallback(() => {
    if (selectedOrderNos.length === 0) {
      alert(t('manager.modules.newOrders.alerts.selectAtLeastOneOrder', {}, 'Velg minst en ordre før du kjører regler'));
      return;
    }
    // Pre-select the configured rule group from SystemSetup
    if (stdRuleGroupID) {
      setSelectedRuleGroupId(stdRuleGroupID);
    }
    setShowRuleModal(true);
    setRuleResult(null);
  }, [selectedOrderNos.length, stdRuleGroupID, t]);

  // Execute rule group for selected orders — kjører sjekk først og spør om konsolidering ved treff
  const executeRuleForSelectedOrders = async () => {
    if (!user?.companyNo) return;

    if (selectedRuleGroupId === 0) {
      alert(t('manager.modules.newOrders.alerts.selectRuleGroup', {}, 'Velg en regelgruppe'));
      return;
    }

    if (selectedOrderNos.length === 0) {
      alert(t('manager.modules.newOrders.alerts.noOrderSelected', {}, 'Ingen ordre valgt'));
      return;
    }

    // Konsoliderings-sjekk: ville valgte ordrer blitt slått sammen med hverandre
    // eller med en eksisterende åpen plukkliste? I så fall: spør operatøren.
    setExecutingRule(true);
    try {
      const check = await ruleGroupEngineService.checkConsolidation(
        user.companyNo, selectedRuleGroupId, selectedOrderNos);

      if (check.wouldConsolidate) {
        setExecutingRule(false);
        setConsolidationCheck(check); // åpner Ja/Nei-spørsmålet
        return;
      }
    } catch (err) {
      // Sjekken er en ren komfort-funksjon — feiler den, kjør som før (med konsolidering)
      console.warn('Consolidation check failed - continuing with default behavior:', err);
    }

    await runRuleGroup(false);
  };

  // Selve regelkjøringen. disableConsolidation = true gir én plukkliste per ordre
  // og hopper over sammenslåing med eksisterende åpne plukklister.
  const runRuleGroup = async (disableConsolidation: boolean) => {
    if (!user?.companyNo) return;

    setConsolidationCheck(null);
    setExecutingRule(true);
    setRuleResult(null);

    try {
      const orderNoList = selectedOrderNos.join(',');
      const sqlFilter = `Orders.OrderNo IN (${orderNoList})`;

      const parameters: RuleGroupParameters = {
        ruleGroupID: selectedRuleGroupId,
        companyNo: user.companyNo,
        warehouseID: warehouseId,
        sessionID: `SESSION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        debugMode: true,
        sqlExtraFilter: sqlFilter,
        disableConsolidation: disableConsolidation
      };

      const result = await ruleGroupEngineService.executeRuleGroup(user.companyNo, parameters);

      setRuleResult(result);
      setShowResultModal(true);
      setShowRuleModal(false);

      if (result.success) {
        setTimeout(() => {
          loadOrders();
          setSelectedOrderNos([]); // Clear selection after successful rule execution
        }, 1000);
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('manager.modules.newOrders.alerts.ruleExecutionError', {}, 'Feil ved kjøring av regel');
      alert(`${t('common.status.error', {}, 'Feil')}: ${errorMsg}`);
      console.error('Error executing rule:', err);
    } finally {
      setExecutingRule(false);
    }
  };

  const formatExecutionTime = (timeSpan: string): string => {
    return ruleGroupEngineService.formatExecutionTime(timeSpan);
  };

  // Delete order handler
  const handleDeleteOrder = useCallback(async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'order') return;
    setDeleting(true);
    try {
      await ordersService.deleteOrder(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('manager.modules.newOrders.alerts.deleteOrderFailed', {}, 'Kunne ikke slette ordre'));
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, t]);

  // Delete order line handler
  const handleDeleteOrderLine = useCallback(async () => {
    if (!deleteConfirm || deleteConfirm.type !== 'line') return;
    setDeleting(true);
    try {
      await ordersService.deleteOrderLine(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('manager.modules.newOrders.alerts.deleteOrderLineFailed', {}, 'Kunne ikke slette ordrelinje'));
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, t]);

  // Row actions for order table
  const rowActions: RowAction<OrderTableRow>[] = useMemo(() => [
    {
      id: 'delete-order',
      label: t('manager.modules.newOrders.actions.deleteOrder', {}, 'Slett ordre'),
      icon: Trash2,
      variant: 'danger' as const,
      disabled: (row: OrderTableRow) => row.lines.some(l => l.pListID && l.pListID > 0),
      onClick: (row: OrderTableRow) => {
        setDeleteConfirm({
          type: 'order',
          id: row.dbId,
          label: t('manager.modules.newOrders.deleteModal.orderLabel', { '0': row.orderNo, '1': row.lineCount }, `ordre ${row.orderNo} (${row.lineCount} linjer)`)
        });
      }
    }
  ], [t]);

  // #2 oppslagsfilter: kildelister for dropdown-filteret på oppslagskolonnene.
  // Filtergruppe-kolonnen viser `filterGroupID`, så filterverdien er ID-en.
  const filterGroupFilterOptions = useMemo(
    () => filterGroups.map(fg => ({
      value: fg.filterGroupID,
      label: `${fg.filterGroupID} - ${fg.filterGroupDescr || ''}`
    })),
    [filterGroups]
  );

  // Define columns for order header table - all available fields
  const columns: ColumnDefinition<OrderTableRow>[] = useMemo(() => [
    // === TIDSLINJE-KNAPP ===
    columnFactory.createTextColumn('_timeline' as any, '_timeline' as any, '', {
      width: 44,
      sortable: false,
      renderer: (_value, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setTimelineOrderNo(row.orderNo); }}
          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
          title={t('manager.modules.newOrders.actions.viewTimeline', {}, 'Vis ordretidslinje')}
        >
          <Clock size={18} />
        </button>
      )
    }),
    // Core order info
    columnFactory.createNumberColumn('orderNo', 'orderNo', t('manager.modules.newOrders.columns.orderNo', {}, 'Ordrenr'), {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value !== null && value !== undefined ? String(value) : '-'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.newOrders.columns.orderNoERP', {}, 'ERP Ordrenr'), {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('orderStatus', 'orderStatus', t('manager.modules.newOrders.columns.orderStatus', {}, 'Ordrestatus'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      // Kode-basert, språk-bevisst filter (verdi=kode, matcher rad-ens orderStatus).
      filterOptions: orderStatusOptions.map(o => ({ value: String(o.value), label: o.label })),
      renderer: (_value, row) => <StatusBadge value={row.orderStatus} statusType={1} />
    }),
    columnFactory.createTextColumn('orderType', 'orderType', t('manager.modules.newOrders.columns.orderType', {}, 'Ordretype'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('orderStatusERP', 'orderStatusERP', t('manager.modules.newOrders.columns.orderStatusERP', {}, 'ERP Status'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('pickListNoERP', 'pickListNoERP', t('manager.modules.newOrders.columns.pickListNoERP', {}, 'Plukkliste ERP'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Customer info
    columnFactory.createTextColumn('customerNo', 'customerNo', t('manager.modules.newOrders.columns.customerNo', {}, 'Kundenr'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('customerName', 'customerName', t('warehouse.general.customerName', {}, 'Kundenavn'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    columnFactory.createTextColumn('customerType', 'customerType', t('manager.modules.newOrders.columns.customerType', {}, 'Kundetype'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp1', 'customerGrp1', t('manager.modules.newOrders.columns.customerGrp1', {}, 'Kundegruppe 1'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp2', 'customerGrp2', t('manager.modules.newOrders.columns.customerGrp2', {}, 'Kundegruppe 2'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerGrp3', 'customerGrp3', t('manager.modules.newOrders.columns.customerGrp3', {}, 'Kundegruppe 3'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('contactName', 'contactName', t('manager.modules.newOrders.columns.contactName', {}, 'Kontaktperson'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('telephone', 'telephone', t('manager.modules.newOrders.columns.telephone', {}, 'Telefon'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('mobTelephone', 'mobTelephone', t('manager.modules.newOrders.columns.mobTelephone', {}, 'Mobiltelefon'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('emailAddress', 'emailAddress', t('manager.modules.newOrders.columns.emailAddress', {}, 'E-post'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Dates
    columnFactory.createDateColumn('orderDate', 'orderDate', t('manager.modules.newOrders.columns.orderDate', {}, 'Ordredato'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('deliveryDate', 'deliveryDate', t('manager.modules.newOrders.columns.deliveryDate', {}, 'Leveringsdato'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      renderer: renderEditableDate('deliveryDate')
    }),
    columnFactory.createDateColumn('arrivalDate', 'arrivalDate', t('manager.modules.newOrders.columns.arrivalDate', {}, 'Ankomstdato'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableDate('arrivalDate')
    }),
    columnFactory.createNumberColumn('orderDeliveryYear', 'orderDeliveryYear', t('manager.modules.newOrders.columns.orderDeliveryYear', {}, 'Lev.år'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryMonth', 'orderDeliveryMonth', t('manager.modules.newOrders.columns.orderDeliveryMonth', {}, 'Lev.mnd'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryWeek', 'orderDeliveryWeek', t('manager.modules.newOrders.columns.orderDeliveryWeek', {}, 'Lev.uke'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('orderDeliveryDay', 'orderDeliveryDay', t('manager.modules.newOrders.columns.orderDeliveryDay', {}, 'Lev.dag'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('customerDeliveryDay', 'customerDeliveryDay', t('manager.modules.newOrders.columns.customerDeliveryDay', {}, 'Kunde lev.dag'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Delivery address
    columnFactory.createNumberColumn('delAddressNo', 'delAddressNo', t('manager.modules.newOrders.columns.delAddressNo', {}, 'Lev.adresse nr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCustomerName', 'delCustomerName', t('manager.modules.newOrders.columns.delCustomerName', {}, 'Lev.kundenavn'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress1', 'delAddress1', t('manager.modules.newOrders.columns.delAddress1', {}, 'Lev.adresse 1'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress2', 'delAddress2', t('manager.modules.newOrders.columns.delAddress2', {}, 'Lev.adresse 2'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delAddress3', 'delAddress3', t('manager.modules.newOrders.columns.delAddress3', {}, 'Lev.adresse 3'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delZipCode', 'delZipCode', t('manager.modules.newOrders.columns.delZipCode', {}, 'Postnr'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCity', 'delCity', t('manager.modules.newOrders.columns.delCity', {}, 'Poststed'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryCode', 'delCountryCode', t('manager.modules.newOrders.columns.delCountryCode', {}, 'Landkode'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('delCountryName', 'delCountryName', t('manager.modules.newOrders.columns.delCountryName', {}, 'Land'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryGLN', 'deliveryGLN', t('manager.modules.newOrders.columns.deliveryGLN', {}, 'Leverings-GLN'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('deliveryAlias', 'deliveryAlias', t('manager.modules.newOrders.columns.deliveryAlias', {}, 'Lev.alias'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('dropPointID', 'dropPointID', t('manager.modules.newOrders.columns.dropPointID', {}, 'Hentepunkt ID'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Warehouse and logistics
    columnFactory.createTextColumn('warehouse', 'warehouse', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 80,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('deliveryMethod', 'deliveryMethod', t('manager.modules.newOrders.columns.deliveryMethod', {}, 'Leveringsmåte'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: true,
      renderer: renderDeliveryMethodDropdown('description')
    }),
    columnFactory.createNumberColumn('deliveryMethodNo', 'deliveryMethodNo', t('manager.modules.newOrders.columns.deliveryMethodNo', {}, 'Lev.måte nr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderDeliveryMethodDropdown('number')
    }),
    columnFactory.createTextColumn('temsOfDelivery', 'temsOfDelivery', t('manager.modules.newOrders.columns.temsOfDelivery', {}, 'Leveringsbetingelser'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('shipmentType', 'shipmentType', t('manager.modules.newOrders.columns.shipmentType', {}, 'Forsendelsestype'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('carrierName', 'carrierName', t('warehouse.general.carrier', {}, 'Transportør'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      enableDropdownFilter: true, // distinct-verdier fra dataene (ikke oppslag)
      visible: false,
      renderer: renderEditableText('carrierName')
    }),
    columnFactory.createNumberColumn('shipmentID', 'shipmentID', t('manager.modules.newOrders.columns.shipmentID', {}, 'Forsendelse ID'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('consignmentSWProductNo', 'consignmentSWProductNo', t('manager.modules.newOrders.columns.consignmentSWProductNo', {}, 'Fraktprodukt'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createNumberColumn('tripID', 'tripID', t('manager.modules.newOrders.columns.tripID', {}, 'Tur ID'), {
      width: 80,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('district', 'district', t('manager.modules.newOrders.columns.district', {}, 'Distrikt'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('district')
    }),
    columnFactory.createNumberColumn('districtNo', 'districtNo', t('manager.modules.newOrders.columns.districtNo', {}, 'Distrikt nr'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableNumber('districtNo')
    }),

    // Loading address
    columnFactory.createTextColumn('loadingName', 'loadingName', t('manager.modules.newOrders.columns.loadingName', {}, 'Lastenavn'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('loadingAddress1', 'loadingAddress1', t('manager.modules.newOrders.columns.loadingAddress1', {}, 'Lasteadresse'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('loadingZipCode', 'loadingZipCode', t('manager.modules.newOrders.columns.loadingZipCode', {}, 'Laste postnr'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('loadingCity', 'loadingCity', t('manager.modules.newOrders.columns.loadingCity', {}, 'Laste poststed'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('loadingCountry', 'loadingCountry', t('manager.modules.newOrders.columns.loadingCountry', {}, 'Laste land'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // References
    columnFactory.createTextColumn('customerOrderNo', 'customerOrderNo', t('manager.modules.newOrders.columns.customerOrderNo', {}, 'Kundens ordrenr'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('yourRef', 'yourRef', t('manager.modules.newOrders.columns.yourRef', {}, 'Deres ref.'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('ourRef', 'ourRef', t('manager.modules.newOrders.columns.ourRef', {}, 'Vår ref.'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('ourRef')
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', t('manager.modules.newOrders.columns.projectNo', {}, 'Prosjektnr'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('projectNo')
    }),
    columnFactory.createTextColumn('depNo', 'depNo', t('manager.modules.newOrders.columns.depNo', {}, 'Avdelingsnr'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('depNo')
    }),
    columnFactory.createTextColumn('employee', 'employee', t('manager.modules.newOrders.columns.employee', {}, 'Ansatt'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('clientID', 'clientID', t('manager.modules.newOrders.columns.clientID', {}, 'Klient ID'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Financial
    columnFactory.createCurrencyColumn('orderTotal', 'orderTotal', t('manager.modules.newOrders.columns.orderTotal', {}, 'Ordretotal'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => ordersService.formatCurrency(value),
      visible: false
    }),
    columnFactory.createTextColumn('termsOfPayment', 'termsOfPayment', t('manager.modules.newOrders.columns.termsOfPayment', {}, 'Betalingsbetingelser'), {
      width: 150,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Priority
    columnFactory.createNumberColumn('orderPriority', 'orderPriority', t('manager.modules.newOrders.columns.orderPriority', {}, 'Ordreprioritet'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: true,
      renderer: renderEditableNumber('orderPriority')
    }),
    columnFactory.createNumberColumn('customerPriority', 'customerPriority', t('manager.modules.newOrders.columns.customerPriority', {}, 'Kundeprioritet'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableNumber('customerPriority')
    }),

    // Boolean flags
    columnFactory.createTextColumn('isRemainderYesNo', 'isRemainderYesNo', t('manager.modules.newOrders.columns.isRemainderYesNo', {}, 'Er restordre'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newOrders.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newOrders.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('createRemainderYesNo', 'createRemainderYesNo', t('manager.modules.newOrders.columns.createRemainderYesNo', {}, 'Opprett restordre'), {
      width: 130,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newOrders.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newOrders.common.no', {}, 'Nei') : '-',
      visible: false
    }),
    columnFactory.createTextColumn('distributionYesNo', 'distributionYesNo', t('manager.modules.newOrders.columns.distributionYesNo', {}, 'Distribusjon'), {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => value === true ? t('manager.modules.newOrders.common.yes', {}, 'Ja') : value === false ? t('manager.modules.newOrders.common.no', {}, 'Nei') : '-',
      visible: false
    }),

    // Notifications
    columnFactory.createTextColumn('emailNotificationAddress', 'emailNotificationAddress', t('manager.modules.newOrders.columns.emailNotificationAddress', {}, 'Varsel e-post'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('smsNotificationAddress', 'smsNotificationAddress', t('manager.modules.newOrders.columns.smsNotificationAddress', {}, 'Varsel SMS'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('intEmailNotificationAddress', 'intEmailNotificationAddress', t('manager.modules.newOrders.columns.intEmailNotificationAddress', {}, 'Int. varsel e-post'), {
      width: 180,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('intSMSNotificationAddress', 'intSMSNotificationAddress', t('manager.modules.newOrders.columns.intSMSNotificationAddress', {}, 'Int. varsel SMS'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Event customer references
    columnFactory.createTextColumn('eventCustomerRef1', 'eventCustomerRef1', t('manager.modules.newOrders.columns.eventCustomerRef1', {}, 'Hend.kunderef 1'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('eventCustomerRef2', 'eventCustomerRef2', t('manager.modules.newOrders.columns.eventCustomerRef2', {}, 'Hend.kunderef 2'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('eventCustomerRef3', 'eventCustomerRef3', t('manager.modules.newOrders.columns.eventCustomerRef3', {}, 'Hend.kunderef 3'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Filter and grouping
    columnFactory.createTextColumn('filterGroupID', 'filterGroupID', t('manager.modules.newOrders.columns.filterGroupID', {}, 'Filtergruppe'), {
      width: 200,
      sortable: true,
      filterType: 'dropdown',
      filterOptions: filterGroupFilterOptions,
      visible: false,
      renderer: renderFilterGroupDropdown
    }),

    // Free fields - PList
    columnFactory.createTextColumn('pListFreeField1', 'pListFreeField1', t('manager.modules.newOrders.columns.pListFreeField1', {}, 'PL Frifelt 1'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('pListFreeField1')
    }),
    columnFactory.createTextColumn('pListFreeField2', 'pListFreeField2', t('manager.modules.newOrders.columns.pListFreeField2', {}, 'PL Frifelt 2'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('pListFreeField2')
    }),
    columnFactory.createTextColumn('pListFreeField3', 'pListFreeField3', t('manager.modules.newOrders.columns.pListFreeField3', {}, 'PL Frifelt 3'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('pListFreeField3')
    }),

    // Free fields - Order (1-16)
    columnFactory.createTextColumn('orderFreeField1', 'orderFreeField1', t('manager.modules.newOrders.columns.orderFreeField1', { '0': 1 }, 'Frifelt 1'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField1')
    }),
    columnFactory.createTextColumn('orderFreeField2', 'orderFreeField2', t('manager.modules.newOrders.columns.orderFreeField2', { '0': 2 }, 'Frifelt 2'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField2')
    }),
    columnFactory.createTextColumn('orderFreeField3', 'orderFreeField3', t('manager.modules.newOrders.columns.orderFreeField3', { '0': 3 }, 'Frifelt 3'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField3')
    }),
    columnFactory.createTextColumn('orderFreeField4', 'orderFreeField4', t('manager.modules.newOrders.columns.orderFreeField4', { '0': 4 }, 'Frifelt 4'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField4')
    }),
    columnFactory.createTextColumn('orderFreeField5', 'orderFreeField5', t('manager.modules.newOrders.columns.orderFreeField5', { '0': 5 }, 'Frifelt 5'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField5')
    }),
    columnFactory.createTextColumn('orderFreeField6', 'orderFreeField6', t('manager.modules.newOrders.columns.orderFreeField6', { '0': 6 }, 'Frifelt 6'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField6')
    }),
    columnFactory.createTextColumn('orderFreeField7', 'orderFreeField7', t('manager.modules.newOrders.columns.orderFreeField7', { '0': 7 }, 'Frifelt 7'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField7')
    }),
    columnFactory.createTextColumn('orderFreeField8', 'orderFreeField8', t('manager.modules.newOrders.columns.orderFreeField8', { '0': 8 }, 'Frifelt 8'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField8')
    }),
    columnFactory.createTextColumn('orderFreeField9', 'orderFreeField9', t('manager.modules.newOrders.columns.orderFreeField9', { '0': 9 }, 'Frifelt 9'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField9')
    }),
    columnFactory.createTextColumn('orderFreeField10', 'orderFreeField10', t('manager.modules.newOrders.columns.orderFreeField10', { '0': 10 }, 'Frifelt 10'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField10')
    }),
    columnFactory.createTextColumn('orderFreeField11', 'orderFreeField11', t('manager.modules.newOrders.columns.orderFreeField11', { '0': 11 }, 'Frifelt 11'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField11')
    }),
    columnFactory.createTextColumn('orderFreeField12', 'orderFreeField12', t('manager.modules.newOrders.columns.orderFreeField12', { '0': 12 }, 'Frifelt 12'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField12')
    }),
    columnFactory.createTextColumn('orderFreeField13', 'orderFreeField13', t('manager.modules.newOrders.columns.orderFreeField13', { '0': 13 }, 'Frifelt 13'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField13')
    }),
    columnFactory.createTextColumn('orderFreeField14', 'orderFreeField14', t('manager.modules.newOrders.columns.orderFreeField14', { '0': 14 }, 'Frifelt 14'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField14')
    }),
    columnFactory.createTextColumn('orderFreeField15', 'orderFreeField15', t('manager.modules.newOrders.columns.orderFreeField15', { '0': 15 }, 'Frifelt 15'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField15')
    }),
    columnFactory.createTextColumn('orderFreeField16', 'orderFreeField16', t('manager.modules.newOrders.columns.orderFreeField16', { '0': 16 }, 'Frifelt 16'), {
      width: 130,
      sortable: true,
      filterType: 'both',
      visible: false,
      renderer: renderEditableText('orderFreeField16')
    }),

    // Audit fields
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.newOrders.columns.createdBy', {}, 'Opprettet av'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.newOrders.columns.created', {}, 'Opprettet'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.newOrders.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 120,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.newOrders.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 140,
      sortable: true,
      filterType: 'both',
      visible: false
    }),

    // Line count (always last and visible)
    columnFactory.createNumberColumn('lineCount', 'lineCount', t('manager.modules.newOrders.columns.lineCount', {}, 'Ant. linjer'), {
      width: 90,
      sortable: true,
      filterType: 'none'
    })
  ], [renderEditableText, renderEditableNumber, renderEditableDate, renderFilterGroupDropdown, renderDeliveryMethodDropdown, orderStatusOptions, filterGroupFilterOptions, t]);

  // Detail renderer for order lines (read-only, no selection) - with delete button
  const renderOrderLines = useCallback((order: OrderTableRow, lines: OrderLineDto[]) => {
    const isBoundToPickList = (line: OrderLineDto) => !!(line.pListID && line.pListID > 0);

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>{t('manager.modules.newOrders.orderLines.title', { '0': order.orderNo }, `Ordrelinjer for ordre ${order.orderNo}`)}</span>
        </h4>

        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-100">
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newOrders.orderLines.lineNo', {}, 'Linjenr')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newOrders.orderLines.itemNo', {}, 'Varenr')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.description', {}, 'Beskrivelse')}</th>
              <th className="text-right py-2 px-3 font-medium text-slate-500">{t('common.fields.quantity', {}, 'Antall')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('manager.modules.newOrders.orderLines.unit', {}, 'Enhet')}</th>
              <th className="text-right py-2 px-3 font-medium text-slate-500">{t('manager.modules.newOrders.orderLines.price', {}, 'Pris')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('common.fields.status', {}, 'Status')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
              <th className="text-left py-2 px-3 font-medium text-slate-500">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
              <th className="w-10 py-2 px-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {lines.map((line) => (
              <tr key={line.id} className="hover:bg-slate-100 transition-colors">
                <td className="py-2 px-3 text-slate-900">{line.lineNo}</td>
                <td className="py-2 px-3 text-slate-900 font-mono">{line.itemNo}</td>
                <td className="py-2 px-3 text-slate-900">{line.description1 || '-'}</td>
                <td className="py-2 px-3 text-slate-900 text-right font-medium">
                  {ordersService.formatQuantity(line.quantity)}
                </td>
                <td className="py-2 px-3 text-slate-900">{line.itemUnitCode || '-'}</td>
                <td className="py-2 px-3 text-slate-900 text-right">
                  {ordersService.formatCurrency(line.price)}
                </td>
                <td className="py-2 px-3">
                  <StatusBadge value={line.lineStatus} statusType={1} />
                </td>
                <td className="py-2 px-3 text-slate-900">{line.warehouse || '-'}</td>
                <td className="py-2 px-3 text-slate-900">{line.stdLocationName || '-'}</td>
                <td className="py-2 px-3 text-center">
                  {!isBoundToPickList(line) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm({
                          type: 'line',
                          id: line.id,
                          label: t('manager.modules.newOrders.deleteModal.lineLabel', { '0': line.lineNo, '1': line.itemNo, '2': order.orderNo }, `ordrelinje ${line.lineNo} (${line.itemNo}) på ordre ${order.orderNo}`)
                        });
                      }}
                      className="p-1 rounded text-red-600 hover:bg-red-100 transition-colors"
                      title={t('manager.modules.newOrders.actions.deleteOrderLine', {}, 'Slett ordrelinje')}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [t]);

  // Expandable configuration
  const expandableConfig: ExpandableRowConfig<OrderTableRow, OrderLineDto[]> = useMemo(() => ({
    getDetailData: (order) => order.lines,
    renderDetail: renderOrderLines,
    expandOnRowClick: false
  }), [renderOrderLines]);

  // Table actions (appears next to "Kolonner" button)
  const tableActions = useMemo(() => [
    {
      id: 'run-rule',
      label: t('manager.modules.newOrders.actions.runRule', { '0': selectedOrderNos.length }, `Kjor regel (${selectedOrderNos.length})`),
      icon: Zap,
      onClick: () => openRuleModal(),
      disabled: () => selectedOrderNos.length === 0,
      variant: 'primary' as const
    },
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadOrders(),
      variant: 'secondary' as const
    }
  ], [selectedOrderNos.length, openRuleModal, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin" size={20} />
          <span>{t('manager.modules.newOrders.status.loadingOrders', {}, 'Laster ordre...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <h3 className="text-red-800 font-medium">{t('manager.modules.newOrders.errors.loadFailed', {}, 'Feil ved lasting av ordre')}</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={loadOrders}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
        >
          <RefreshCw size={16} />
          <span>{t('manager.modules.newOrders.actions.retry', {}, 'Prov igjen')}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <Package size={24} />
            <span>{t('manager.modules.newOrders.title', {}, 'Nye Ordre')}</span>
          </h1>
        </div>

        {/* Info toggle */}
        <button
          type="button"
          onClick={() => setShowInfo(prev => !prev)}
          className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
          aria-expanded={showInfo}
        >
          <Info size={14} />
          <span>{t('manager.modules.newOrders.info.toggle', {}, 'Hva er Nye Ordre?')}</span>
          {showInfo ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {showInfo && (
          <div className="mt-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-800">
            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newOrders.info.purposeHeading', {}, 'Hensikt')}</h3>
              <p>
                {t('manager.modules.newOrders.info.purposeBody', {}, 'Nye Ordre viser salgsordre som er klare for plukking, men ennå ikke fordelt til en plukkliste. Herfra kan du kjøre regelmotoren for å generere plukklister, eller redigere/slette ordre før de plukkes.')}
              </p>
            </div>

            <div className="mb-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newOrders.info.criteriaHeading', {}, 'Utvalgskriterier')}</h3>
              <p className="mb-1">{t('manager.modules.newOrders.info.criteriaIntro', {}, 'En ordre vises i listen når:')}</p>
              <ol className="list-decimal pl-5 space-y-0.5">
                <li>
                  {t('manager.modules.newOrders.info.criteria1a', {}, 'Ordren har minst én')} <strong>{t('manager.modules.newOrders.info.criteria1Strong', {}, 'ordrelinje')}</strong> {t('manager.modules.newOrders.info.criteria1b', {}, 'som ikke er knyttet til en plukkliste')}
                  (<code className="bg-blue-100 px-1 rounded text-xs">PListID = 0</code>)
                </li>
                <li>
                  {t('manager.modules.newOrders.info.criteria2a', {}, 'Ordrelinjen tilhører')} <strong>{t('manager.modules.newOrders.info.criteria2Strong', {}, 'valgt lager')}</strong> {t('manager.modules.newOrders.info.criteria2b', {}, '(filteret øverst, f.eks. "Hovedlager")')}
                </li>
              </ol>
              <p className="mt-1">
                {t('manager.modules.newOrders.info.criteriaNote', {}, 'Kun ordrelinjer uten plukkliste vises i ordredetaljene — linjer som allerede er på en plukkliste skjules.')}
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h3 className="font-bold text-gray-900 mb-1">{t('manager.modules.newOrders.info.cautionHeading', {}, 'Pass på')}</h3>
              <ul className="list-disc pl-5 space-y-0.5 text-gray-800">
                <li>
                  {t('manager.modules.newOrders.info.caution1', {}, 'Ordre med linjer fordelt på flere lagre vises i hvert relevant lager, men kun med linjene som tilhører det aktuelle lageret')}
                </li>
                <li>
                  {t('manager.modules.newOrders.info.caution2', {}, 'Sletting av en plukkliste fører til at ordrelinjene blir "frigjort" og dukker opp her igjen — klar for ny regelmotorkjøring')}
                </li>
                <li>
                  {t('manager.modules.newOrders.info.caution3a', {}, 'Når en ordre er fullt plukket (alle linjer har fått')}{' '}
                  <code className="bg-yellow-100 px-1 rounded text-xs">PListID &gt; 0</code>{t('manager.modules.newOrders.info.caution3b', {}, '), forsvinner den automatisk fra listen')}
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 py-2">
        <WarehouseTable
          data={tableData}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: false,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            },
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'new-orders-table'
          }}
          selectedRows={selectedOrderNos}
          onSelectionChange={handleSelectionChange}
          getRowId={getOrderRowId}
          tableActions={tableActions}
          rowActions={rowActions}
          expandableConfig={expandableConfig}
          loading={false}
          error={undefined}
          aria-label={t('manager.modules.newOrders.table.ariaLabel', {}, 'Nye ordre tabell')}
        />
      </div>

      {/* Rule Execution Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <Zap className="text-purple-600" size={24} />
                <span>{t('manager.modules.newOrders.ruleModal.title', {}, 'Kjor Regelgruppe')}</span>
              </h2>
              <button
                onClick={() => setShowRuleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Selected Orders Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">{t('manager.modules.newOrders.ruleModal.selectedOrders', {}, 'Valgte ordre')}</h3>
                <div className="text-sm text-blue-800">
                  {t('manager.modules.newOrders.ruleModal.ordersSelectedCount', { '0': selectedOrderNos.length }, `${selectedOrderNos.length} ordre valgt`)}: {selectedOrderNos.join(', ')}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  SQL Filter: Orders.OrderNo IN ({selectedOrderNos.join(',')})
                </div>
              </div>

              {/* Rule Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager.modules.newOrders.ruleModal.ruleGroupLabel', {}, 'Regelgruppe *')}
                </label>
                <select
                  value={selectedRuleGroupId}
                  onChange={(e) => setSelectedRuleGroupId(parseInt(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={executingRule}
                >
                  <option value={0}>{t('manager.modules.newOrders.ruleModal.selectRuleGroupPlaceholder', {}, 'Velg regelgruppe...')}</option>
                  {ruleGroups.map((rg) => (
                    <option key={rg.ruleGroupID} value={rg.ruleGroupID}>
                      {rg.ruleGroupID} - {rg.ruleGroupName} {rg.activeYesNo ? t('manager.modules.newOrders.ruleModal.active', {}, '(Aktiv)') : t('manager.modules.newOrders.ruleModal.inactive', {}, '(Inaktiv)')}
                    </option>
                  ))}
                </select>
                {selectedRuleGroupId > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {ruleGroups.find(rg => rg.ruleGroupID === selectedRuleGroupId)?.description}
                  </div>
                )}
              </div>

              {/* Warehouse ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager.modules.newOrders.ruleModal.warehouseIdLabel', {}, 'Lager ID (valgfritt)')}
                </label>
                <input
                  type="text"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  placeholder={t('manager.modules.newOrders.ruleModal.warehouseIdPlaceholder', {}, 'La sta tom for standard')}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  disabled={executingRule}
                />
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">{t('manager.modules.newOrders.ruleModal.warningTitle', {}, 'Viktig informasjon')}</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>{t('manager.modules.newOrders.ruleModal.warning1', {}, 'Regelen vil kun kjores for de valgte ordrene')}</li>
                      <li>{t('manager.modules.newOrders.ruleModal.warning2', {}, 'Endringer vil bli gjort i databasen')}</li>
                      <li>{t('manager.modules.newOrders.ruleModal.warning3', {}, 'Denne operasjonen kan ikke angres automatisk')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowRuleModal(false)}
                disabled={executingRule}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={executeRuleForSelectedOrders}
                disabled={executingRule || selectedRuleGroupId === 0}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {executingRule ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>{t('manager.modules.newOrders.ruleModal.executing', {}, 'Kjorer...')}</span>
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    <span>{t('manager.modules.newOrders.ruleModal.title', {}, 'Kjor Regelgruppe')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consolidation Prompt Modal — vises når sjekken finner at valgte ordrer ville blitt konsolidert */}
      {consolidationCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="border-b px-6 py-4 flex items-center space-x-2">
              <AlertCircle className="text-purple-600" size={24} />
              <h2 className="text-xl font-bold">
                {t('manager.modules.newOrders.consolidation.title', {}, 'Konsolidering av plukklister')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-700">
                {t('manager.modules.newOrders.consolidation.message', {},
                  'Noen av de valgte ordrene har samme grupperingsnivå, eller det finnes en åpen plukkliste for samme mottaker fra før.')}
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1 text-sm text-blue-800">
                {consolidationCheck.groups
                  .filter(g => g.orderNos.length > 1 || g.existingPListId)
                  .map((g, idx) => (
                    <div key={idx}>
                      {g.existingPListId
                        ? t('manager.modules.newOrders.consolidation.mergeWithExisting',
                            { '0': g.orderNos.join(', '), '1': g.existingPListId },
                            `Ordre ${g.orderNos.join(', ')} legges til eksisterende plukkliste ${g.existingPListId}`)
                        : t('manager.modules.newOrders.consolidation.mergeWithSelected',
                            { '0': g.orderNos.join(', ') },
                            `Ordre ${g.orderNos.join(', ')} slås sammen til én plukkliste`)}
                    </div>
                  ))}
              </div>

              <p className="text-sm font-medium text-gray-900">
                {t('manager.modules.newOrders.consolidation.question', {}, 'Vil du konsolidere?')}
              </p>
            </div>

            <div className="bg-gray-50 border-t px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setConsolidationCheck(null)}
                disabled={executingRule}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={() => runRuleGroup(true)}
                disabled={executingRule}
                className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50"
              >
                {t('manager.modules.newOrders.consolidation.no', {}, 'Nei, egne plukklister')}
              </button>
              <button
                onClick={() => runRuleGroup(false)}
                disabled={executingRule}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {t('manager.modules.newOrders.consolidation.yes', {}, 'Ja, konsolider')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && ruleResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                {ruleResult.success ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <AlertCircle className="text-red-600" size={24} />
                )}
                <span>{t('manager.modules.newOrders.resultModal.title', {}, 'Regelkjoring Resultat')}</span>
              </h2>
              <button
                onClick={() => setShowResultModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Summary */}
              <div className={`border rounded-lg p-4 ${
                ruleResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`font-medium text-lg ${
                    ruleResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {ruleResult.success ? t('manager.modules.newOrders.resultModal.success', {}, 'Suksess') : t('common.status.error', {}, 'Feil')}
                  </span>
                  <span className="text-sm text-gray-600">
                    {formatExecutionTime(ruleResult.totalExecutionTime)}
                  </span>
                </div>
                <p className={`text-sm ${
                  ruleResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {ruleResult.message}
                </p>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-600 mb-1">{t('manager.modules.newOrders.resultModal.rulesExecuted', {}, 'Regler utfort')}</div>
                  <div className="text-2xl font-bold text-blue-900">{ruleResult.totalRulesExecuted}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm text-green-600 mb-1">{t('manager.modules.newOrders.resultModal.success', {}, 'Suksess')}</div>
                  <div className="text-2xl font-bold text-green-900">{ruleResult.totalRulesSucceeded}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-600 mb-1">{t('common.status.error', {}, 'Feil')}</div>
                  <div className="text-2xl font-bold text-red-900">{ruleResult.totalRulesFailed}</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="text-sm text-purple-600 mb-1">{t('manager.modules.newOrders.resultModal.recordsAffected', {}, 'Poster pavirket')}</div>
                  <div className="text-2xl font-bold text-purple-900">{ruleResult.totalRecordsAffected}</div>
                </div>
              </div>

              {/* Errors */}
              {ruleResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-medium text-red-900 mb-2">{t('common.status.error', {}, 'Feil')}</h3>
                  <div className="space-y-1">
                    {ruleResult.errors.map((error, idx) => (
                      <div key={idx} className="text-sm text-red-700">* {error}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {ruleResult.warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-medium text-yellow-900 mb-2">{t('manager.modules.newOrders.resultModal.warnings', {}, 'Advarsler')}</h3>
                  <div className="space-y-1">
                    {ruleResult.warnings.map((warning, idx) => (
                      <div key={idx} className="text-sm text-yellow-700">* {warning}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Rule Results */}
              {ruleResult.ruleResults.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">{t('manager.modules.newOrders.resultModal.detailedResults', {}, 'Detaljerte regelresultater')}</h3>
                  <div className="space-y-2">
                    {ruleResult.ruleResults.map((result, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {result.success ? (
                              <CheckCircle size={16} className="text-green-600" />
                            ) : (
                              <AlertCircle size={16} className="text-red-600" />
                            )}
                            <span className="font-medium text-sm">
                              {t('manager.modules.newOrders.resultModal.ruleLabel', { '0': result.ruleID, '1': result.ruleName }, `Regel ${result.ruleID}: ${result.ruleName}`)}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatExecutionTime(result.executionTime)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">{result.message}</div>
                        {result.recordsAffected > 0 && (
                          <div className="text-xs text-gray-500">
                            {t('manager.modules.newOrders.resultModal.recordsAffectedLine', { '0': result.recordsAffected }, `Poster pavirket: ${result.recordsAffected}`)}
                          </div>
                        )}
                        {result.errors.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            {result.errors.map((err, errIdx) => (
                              <div key={errIdx}>* {err}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowResultModal(false)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                {t('common.buttons.close', {}, 'Lukk')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Timeline Modal */}
      {timelineOrderNo !== null && (
        <OrderTimelineModal
          isOpen={true}
          orderNo={timelineOrderNo}
          onClose={() => setTimelineOrderNo(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex items-center space-x-3">
              <AlertCircle className="text-red-600" size={24} />
              <h2 className="text-lg font-bold text-gray-900">{t('manager.modules.newOrders.deleteModal.title', {}, 'Bekreft sletting')}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700">
                {t('manager.modules.newOrders.deleteModal.confirmQuestion', { '0': deleteConfirm.label }, `Er du sikker pa at du vil slette ${deleteConfirm.label}?`)}
              </p>
              {deleteConfirm.type === 'order' && (
                <p className="text-sm text-red-600 mt-2">
                  {t('manager.modules.newOrders.deleteModal.allLinesWarning', {}, 'Alle ordrelinjer vil ogsa bli slettet.')}
                </p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={deleteConfirm.type === 'order' ? handleDeleteOrder : handleDeleteOrderLine}
                disabled={deleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="animate-spin" size={16} />
                    <span>{t('manager.modules.newOrders.deleteModal.deleting', {}, 'Sletter...')}</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>{t('common.buttons.delete', {}, 'Slett')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewOrders;
